/**
 * Where the client connects, derived from where the page came from.
 *
 * `docs/KNOWN-BUGS.md` item 6: "An `https` page cannot open a `ws://` socket.
 * Always `wss://`, same origin as the client." A hardcoded host would break
 * that the first time the app is served from anywhere but the machine it was
 * written on, and a hardcoded scheme would break it the first time the dev
 * host got a certificate. So the scheme and the authority both come from
 * `location`, and this module is the only place either is decided.
 *
 * `docs/KNOWN-BUGS.md` item 7: "Anything under the API or room paths is
 * NetworkOnly." The two prefixes below are the same two the service worker
 * already refuses to cache (`apps/client/src/pwa/config.ts`), which is why
 * every server route this phase adds lives under one of them.
 */

/** REST prefix: room creation and code lookup. Never cached by the service worker. */
export const API_PATH = '/api';

/** WebSocket and matchmaking prefix. Never cached by the service worker. */
export const ROOM_PATH = '/room';

/** The subset of `window.location` this module needs, so it is testable without a browser. */
export interface OriginLike {
  /** `'http:'` or `'https:'`, including the colon, exactly as `location.protocol` gives it. */
  readonly protocol: string;
  /** Hostname and port, exactly as `location.host` gives it. */
  readonly host: string;
}

/**
 * The WebSocket endpoint Colyseus's client should be pointed at.
 *
 * `https:` maps to `wss:` and `http:` to `ws:`, so a development page served
 * over plain HTTP still works while a deployed page can only ever open a
 * secure socket. Anything else — a `file:` page, an extension origin — is
 * treated as insecure-but-unknown and gets `ws:`, because guessing `wss:`
 * there would fail in a way nobody could debug from the error.
 */
export function socketEndpoint(origin: OriginLike, path: string = ROOM_PATH): string {
  const scheme = origin.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${origin.host}${path}`;
}

/** The same-origin REST base. Relative, so `fetch` resolves it against the page. */
export function apiPath(segment = ''): string {
  if (segment === '') return API_PATH;
  return segment.startsWith('/') ? `${API_PATH}${segment}` : `${API_PATH}/${segment}`;
}

/** `POST` here to create a room. Responds with a code and the room id to join. */
export const API_ROOMS = apiPath('/rooms');

/** `GET /api/rooms/:code` resolves a room code to the id `joinById` needs. */
export function apiRoomByCode(code: string): string {
  return `${API_ROOMS}/${encodeURIComponent(code)}`;
}

/** `GET /api/healthz` — the liveness probe, moved under `/api` so the service worker leaves it alone. */
export const API_HEALTH = apiPath('/healthz');

/** The Colyseus room type name. One room per match. */
export const DUEL_ROOM = 'duel';
